<template>
    <div class="product-offer-group">
        <v-expansion-panel v-if="productOfferGroup" :key="index" class="product-offer-group__panel">
            <v-expansion-panel-title
                class="product-offer-group__panel--header"
                :class="{ 'product-offer-group__panel--header--readonly': isReadOnly }"
                hide-actions
                @keyup.space.prevent
                @keypress.enter.prevent
            >
                <template v-slot:default="{ expanded: slotExpanded }">
                    <div class="product-offer-group__panel--header--content">
                        <div class="product-offer-group__name">
                            <span class="product-offer-group__name--label">
                                <b>{{ $tkey('pg') }}</b>
                            </span>
                            <div class="product-offer-group__name--wrapper" @click.prevent.stop>
                                <v-text-field
                                    ref="pog-name"
                                    class="rtls-text-field rtls-text-field--white product-offer-group__name--text"
                                    :placeholder="$tkey('pgName')"
                                    :model-value="productOfferGroup.description || ''"
                                    :rules="rules"
                                    :bg-color="ui.fieldBgColor"
                                    autocomplete="off"
                                    :disabled="isDisabledInput"
                                    @update:model-value="debouncedUpdateDescription"
                                    @click.stop=""
                                />
                            </div>
                        </div>
                        <div class="product-offer-group__delete">
                            <common-delete-dialog
                                :resource="productOfferGroupsResource"
                                :child-dependencies-warning="false"
                                :disabled="promotion.splitPromotion"
                                @delete="deletePOG()"
                            />
                        </div>
                    </div>
                    <main-expand-button :is-expanded="slotExpanded" />
                </template>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
                <!--Need for intercept keydown event from inside ag-grid components, not works directly from ag-grid-vue-->
                <!--without this common-delete-dialog catch keydown from filter components and open modal-->
                <div style="display: contents" @keydown.enter.prevent.stop>
                    <div
                        class="product-offer-group__category-selector"
                        :style="categorySelectorStyle"
                    >
                        <b class="product-offer-group__category-selector--text">{{
                            $tkey('include')
                        }}</b>
                        <vuex-select
                            class="product-offer-group__category-selector--type"
                            :options="options"
                            item-text="name"
                            item-value="id"
                            :width="ui.selectWidth"
                            :bg-color="ui.fieldBgColor"
                            :vuex-module="vuexModule"
                            :namespace="namespace"
                            :tab="offer"
                            :field-path="fieldPath"
                            :disabled="isDisabledInput"
                            field-name="scope"
                            @change="onPOGScopeChange"
                        />
                        <template v-if="productOfferGroup.scope !== pogScope.storeWide">
                            <b class="product-offer-group__category-selector--text">{{
                                $tkey('from')
                            }}</b>
                            <product-offer-group-categories
                                :namespace="namespace"
                                :pog-index="index"
                                :is-category-wide-promotion="isSelectedPromotionCategoryWide"
                                class="product-offer-group__category-selector--categories"
                                @change="onCategoriesChange"
                            />
                        </template>
                    </div>
                    <div v-if="showAttributesScopeSelector">
                        <attribute-filter-panel
                            ref="attribute-filter-panel"
                            :staging-area-promotion="stagingAreaPromotion"
                            :namespace="namespace"
                            :make-read-only="isAttributeSelectorReadOnly"
                            :product-offer-group-id="productOfferGroup._id"
                        />
                    </div>
                    <template v-if="productOfferGroup.scope === pogScope.selectedProducts">
                        <div class="product-offer-group__filter">
                            <v-btn-toggle
                                v-model="viewSelectedOnly"
                                density="compact"
                                class="product-offer-group__filter--toggle"
                                :mandatory="true"
                                @update:model-value="setViewSelectedOnly"
                            >
                                <v-btn :value="false" :disabled="isReadOnly">
                                    {{ $tkey('all') }}
                                </v-btn>
                                <v-btn :value="true" :disabled="isReadOnly">
                                    {{ $tkey('selected') }}
                                </v-btn>
                            </v-btn-toggle>
                            <v-tooltip location="top" :disabled="!promotionNotSaved">
                                <template v-slot:activator="{ props }">
                                    <div v-bind="props">
                                        <v-btn
                                            class="product-offer-group__add-bulk"
                                            color="primary"
                                            depressed
                                            size="x-small"
                                            :disabled="
                                                isDisabledInput ||
                                                    !hasUserSelectedCategories ||
                                                    promotionNotSaved
                                            "
                                            @click="openBulkDialog"
                                        >
                                            {{
                                                toSentenceCase($t('actions.selectUsingProductKeys'))
                                            }}
                                        </v-btn>
                                    </div>
                                </template>
                                <span>{{ toSentenceCase($tkey('savePromotionBefore')) }}</span>
                            </v-tooltip>
                            <v-btn
                                v-if="motherProductSelectionEnabled"
                                class="product-offer-group__add-bulk"
                                color="primary"
                                depressed
                                size="x-small"
                                :disabled="isMotherProductSelectionDisabled"
                                @click="openMotherProductDialog"
                            >
                                {{ toSentenceCase($tkey('motherProductSelector.button')) }}
                            </v-btn>
                            <vuex-radio-group
                                :label="$tkey('soldBy')"
                                :options="promotionTypeOptions"
                                :getter="() => promotionType"
                                :disabled="promotionHasProducts"
                                :vuex-module="vuexModule"
                                :namespace="namespace"
                                :inline="true"
                                row
                                @change="onProductTypeChange"
                            />
                        </div>
                        <ag-grid-vue
                            v-if="expanded && hasUserSelectedCategories"
                            ref="grid"
                            :class="{
                                'ag-theme-custom': true,
                                'selection-disabled': isSelectAllDisabled || isDisabledSelectHeader,
                            }"
                            :get-row-node-id="getRowNodeId"
                            :modules="modules"
                            :row-data="rowData"
                            :column-defs="columnDefs"
                            :default-col-def="defaultColDef"
                            :selection-column-def="selectionColumnDef"
                            :status-bar="statusBar"
                            :grid-options="gridOptions"
                            :suppress-click-edit="true"
                            :process-cell-from-clipboard="processCellFromClipboard"
                            :process-cell-for-clipboard="processCellForClipboard"
                            style="width: 100%; height: 35rem"
                            @grid-ready="onGridReady"
                            @first-data-rendered="firstDataRendered"
                        />
                    </template>
                </div>
            </v-expansion-panel-text>
        </v-expansion-panel>
        <bulk-dialog
            ref="bulk-product-key-dialog"
            :has-activator="false"
            :promotion-id="namespace"
            :product-offer-group-id="productOfferGroup._id"
        />
        <mother-product-select-dialog
            v-if="motherProductSelectionEnabled"
            ref="mother-product-dialog"
            :options="motherProductOptions"
            @confirm="onMotherProductSelection"
        />
    </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue3';
import { ClientSideRowModelModule } from 'ag-grid-community';
import {
    RichSelectModule,
    StatusBarModule,
    SetFilterModule,
    CellSelectionModule,
    ClipboardModule,
} from 'ag-grid-enterprise';
import {
    debounce,
    filter,
    unionBy,
    uniqBy,
    cloneDeep,
    size,
    map,
    isEmpty,
    get,
    differenceBy,
    find,
    set,
    isNil,
    round,
} from 'lodash';
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import resources from '@enums/resources';
import featureFlags from '@enums/feature-flags';
import productTypes from '@enums/product-types';
import promotionTypes from '@enums/promotion-types';
import pogScope from '@enums/product-offer-group-scope';
import promotionTabs from '@enums/promotion-tabs';
import createFeatureAwareFactory from '@/js/feature-toggles/feature-factory';
import attributeSelectionHeader from './product-attribute-selector-header.vue';
import { toSentenceCase } from '@/js/utils/string-utils';
import promoAgFilterAllRows from './ag-filter-all-rows.vue';
import agGridUtils from '@/js/utils/ag-grid-utils';
import productDetailsMixin from '@/js/components/product-details/product-details-mixin.vue';
import validators from '@/js/validators';
import {
    buildProductFamilyIndex,
    getMotherProductOptions,
    getPartiallySelectedMotherKeys,
    getPriceUpdateProducts,
    getProductFamily,
    getProductFamilyRole,
    isMotherRole,
    withMotherProductKeys,
} from '@/js/pages/planning/utils/mother-product-utils';
import { markRaw, nextTick } from 'vue';
import uxEvents from '@enums/ux-events';
import { updateGridProductPrices } from '@/js/pages/planning/utils/product-price-update-utils';

const ATTRIBUTE_GROUP_WIDTH = 260;
const TEXT_FIELD_BG_COLOR = '#FFFFFF';
const SELECT_WIDTH = '25rem';

const { productOfferGroups: productOfferGroupsResource } = resources;
const {
    canCreateStoreWidePromotion,
    canCreateCategoryWidePromotion,
    allowStoreWidePromotions,
    allowCategoryWidePromotions,
    showOnlyMinPrices,
} = featureFlags;
const { offer } = promotionTabs;

export default {
    localizationKey: 'planning.promotionsMaintenance.offer.offerGroup',

    components: {
        'ag-grid-vue': AgGridVue,
    },

    mixins: [productDetailsMixin],

    props: {
        productOfferGroup: {
            type: Object,
            default: null,
        },
        index: {
            type: Number,
            default: 0,
        },
        expanded: {
            type: Boolean,
            default: () => false,
        },
        namespace: {
            required: true,
            type: String,
        },
        vuexModule: {
            type: String,
            required: true,
        },
        context: {
            type: Object,
            default: null,
        },
    },
    emits: ['delete', 'removed'],
    data() {
        return {
            ui: {
                fieldBgColor: TEXT_FIELD_BG_COLOR,
                selectWidth: SELECT_WIDTH,
            },
            productsForPromotionProductOfferGroup: [],
            productOfferGroupsResource,
            viewSelectedOnly: false,
            rowData: [],
            modules: markRaw([
                ClientSideRowModelModule,
                RichSelectModule,
                StatusBarModule,
                SetFilterModule,
                CellSelectionModule,
                ClipboardModule,
            ]),
            productsInProgress: {},
            gridOptions: markRaw({
                rowSelection: {
                    mode: 'multiRow',
                    selectAll: 'filtered',
                    headerCheckbox: true,
                    checkboxSelection: 'selectionColumn',
                    checkboxes: node => {
                        return !this.isDisabledSelectColumn({ data: node.data });
                    },
                },
                components: { promoAgFilterAllRows, attributeSelectionHeader },
                pagination: false,
                rowBuffer: 75,
                rowHeight: 35, // Specified in pixels.
                headerHeight: 40, // Specified in pixels.
                groupHeaderHeight: 45, // Specified in pixels.
                columnTypes: {
                    numericColumnCustom: agGridUtils.columnTypes.numericColumnCustom,
                    numericColumnCustomHighlightNegative:
                        agGridUtils.columnTypes.numericColumnCustomHighlightNegative,
                },
                getRowId: ({ data }) => String(data.productKey),
                overlayLoadingTemplate: `<span class="ag-overlay-loading-center">${toSentenceCase(
                    this.$t('actions.loading')
                )}</span>`,
                overlayNoRowsTemplate: `<span class="ag-overlay-loading-center">${toSentenceCase(
                    this.$tkey('grid.noRows')
                )}</span>`,
                // need to override navigation methods to prevent focusing next cell when user presses
                // arrow keys in order for navigation to work inside inputs
                navigateToNextCell: () => {
                    return null;
                },
                ensureDomOrder: true,
                cellSelection: true,
                onRowSelected: this.onRowSelected,
                onSelectionChanged: this.onSelectionChanged,
                getMainMenuItems: this.getMainMenuItems,
                tooltipShowDelay: 50,
                enableBrowserTooltips: false,
                context: {
                    isReadOnly: () => this.isReadOnly,
                    readOnlyReason: () => this.propagatedReadOnlyReason,
                },
            }),
            defaultColDef: {
                flex: -1,
                sortable: true,
                filter: true,
                resizable: true,
                filterParams: {
                    newRowsAction: 'keep',
                },
                suppressKeyboardEvent: params => {
                    const { keyCode } = params.event;

                    // allow only ctrl, v, c to allow ctrl + c, ctrl + v
                    // also left and right command keys for mac users
                    return ![17, 86, 67, 91, 93].includes(keyCode);
                },
                suppressHeaderMenuButton: true,
            },
            defaultSortModel: [
                {
                    colId: 'description',
                    sort: 'asc',
                },
            ],
            gridApi: null,
            promotionTypeOptions: [
                {
                    text: this.$tkey(productTypes.unit),
                    value: productTypes.unit,
                },
                {
                    text: this.$tkey(productTypes.weight),
                    value: productTypes.weight,
                },
            ],
            columnDefs: [],
            selectedProductAttributes: [],
            addedProductAccumulatorKeys: new Set(),
            removedProductAccumulatorKeys: new Set(),
            partialMotherKeys: markRaw(new Set()),
            pogScope,
            offer,
            isWeightPromotion: false,
            filteredRowCount: 0,
            pendingRefreshTimer: null,
        };
    },

    computed: {
        ...mapState('clientConfig', ['toggleLogic', 'generalConfig', 'promotionTabs']),
        ...mapState('attributeMetadata', ['attributeMetadata']),
        ...mapState('promotions', ['unsavedPromotion']),
        ...mapGetters('promotions', [
            'selectedPromotion',
            'getStagingAreaPromotionById',
            'isSelectedPromotionCategoryWide',
            'isSelectedPromotionStoreWide',
            'isSelectedPromotionCategoryOrStoreWide',
        ]),
        ...mapGetters('subCampaigns', ['hasAccessToAllSubCampaignCategories']),
        ...mapGetters('context', ['userCategories']),
        ...mapGetters('clientConfig', ['currencySymbol']),
        ...mapGetters('attributeMetadata', ['getDefaultAttributes']),
        showAttributesScopeSelector() {
            if (!this.promotion) {
                return false;
            }
            return this.promotion.promotionType === promotionTypes.attributeScoped;
        },
        isDisabledInput() {
            return this.showAttributesScopeSelector || this.isReadOnly;
        },
        fieldPath() {
            return `productOfferGroups[${this.index}]`;
        },

        hasAccessToAllCategoriesOnPromotion() {
            return isEmpty(
                differenceBy(
                    this.promotion.userSelectedCategories,
                    this.userCategories,
                    'levelEntryKey'
                )
            );
        },

        options() {
            return [
                {
                    id: pogScope.selectedProducts,
                    name: this.$tkey('selectedProducts'),
                    disabled:
                        !this.isSelectedPromotionCategoryOrStoreWide ||
                        (this.isSelectedPromotionCategoryWide &&
                            !this.hasAccessToAllCategoriesOnPromotion),
                },
                ...(this.toggleLogic[allowStoreWidePromotions]
                    ? [
                          {
                              id: pogScope.storeWide,
                              name: this.$tkey('storeWide'),
                              disabled:
                                  !this.toggleLogic[canCreateStoreWidePromotion] ||
                                  this.isSelectedPromotionStoreWide ||
                                  !this.hasAccessToAllSubCampaignCategories,
                          },
                      ]
                    : []),
                ...(this.toggleLogic[allowCategoryWidePromotions]
                    ? [
                          {
                              id: pogScope.categoryWide,
                              name: this.$tkey('categoryWide'),
                              disabled:
                                  !this.toggleLogic[canCreateCategoryWidePromotion] ||
                                  this.isSelectedPromotionCategoryWide,
                          },
                      ]
                    : []),
            ];
        },

        featureAwareFactory() {
            return createFeatureAwareFactory(this.toggleLogic);
        },

        motherProductSelectionEnabled() {
            return !!get(this.generalConfig, 'enableMotherProductSelection', false);
        },

        showOnlyMinimumPrices() {
            return this.toggleLogic[showOnlyMinPrices];
        },

        categorySelectorStyle() {
            if (!this.productOfferGroup.userSelectedCategories.length) {
                return {
                    height: '5rem',
                };
            }
            return {};
        },

        hasUserSelectedCategories() {
            return this.productOfferGroup.userSelectedCategories.length > 0;
        },

        statusBar() {
            return {
                statusPanels: [
                    {
                        statusPanel: 'agTotalAndFilteredRowCountComponent',
                        align: 'left',
                    },
                ],
            };
        },

        promotion() {
            if (!isEmpty(this.selectedPromotion)) return this.selectedPromotion;

            // if creating a new promotion in the parkinglot, promotion will be in staging area and _id is default
            return this.getStagingAreaPromotionById('default');
        },
        stagingAreaPromotion() {
            return this.getStagingAreaPromotionById(this.namespace);
        },

        productKeysOnPromotion() {
            return new Set(get(this.stagingAreaPromotion, 'products', []).map(p => p.productKey));
        },

        productsOnProductOfferGroup() {
            const products = this.addMotherProductKeys(
                filter(this.stagingAreaPromotion.products, product =>
                    this.productKeysOnProductOfferGroup.has(product.productKey)
                )
            );

            return products.map(product => {
                return {
                    ...product,
                    onCurrentProductGroup: true,
                };
            });
        },

        productsOnOtherProductOfferGroups() {
            const products = filter(
                this.productsForPromotionProductOfferGroup,
                product =>
                    this.filterByProductType(product.isUnitProduct) &&
                    this.productKeysOnPromotion.has(product.productKey) &&
                    !this.productKeysOnProductOfferGroup.has(product.productKey)
            );

            return products.map(product => {
                return {
                    ...product,
                    onCurrentProductGroup: false,
                    onAnotherProductGroup: true,
                };
            });
        },

        accessibleCategories() {
            return new Set(
                [
                    ...this.userCategories,
                    ...get(this.productOfferGroup, 'userSelectedCategories', []),
                ].map(cat => cat.levelEntryKey)
            );
        },

        userCategoriesKeys() {
            return new Set(this.userCategories.map(cat => cat.levelEntryKey));
        },

        productKeysOnProductOfferGroup() {
            return new Set(this.productOfferGroup.products);
        },

        numberOfTiers() {
            return size(this.stagingAreaPromotion.offerMechanic.tiers);
        },

        allProductRowsForGrid() {
            const productsNotOnPromotion =
                // cloneDeep needed here otherwise deselection of products does not work properly
                // and checkboxes remain selected

                cloneDeep(
                    this.productsForPromotionProductOfferGroup.filter(product =>
                        this.isWeightPromotion ? !product.isUnitProduct : product.isUnitProduct
                    )
                ) || [];

            const data = unionBy(
                cloneDeep(this.productsOnProductOfferGroup),
                cloneDeep(this.productsOnOtherProductOfferGroups),
                productsNotOnPromotion,
                'productKey'
            );
            return data;
        },

        dataForGrid() {
            return this.allProductRowsForGrid;
        },

        productFamilyIndex() {
            return buildProductFamilyIndex(this.allProductRowsForGrid);
        },

        motherProductOptions() {
            if (!this.motherProductSelectionEnabled) return [];

            return getMotherProductOptions(this.allProductRowsForGrid, this.productFamilyIndex).map(
                option => {
                    const product = this.allProductRowsForGridByKey.get(String(option.productKey));
                    return {
                        ...option,
                        disabled:
                            product.onAnotherProductGroup ||
                            !this.userCategoriesKeys.has(product.category),
                    };
                }
            );
        },

        isMotherProductSelectionDisabled() {
            return (
                this.isDisabledInput ||
                !this.hasUserSelectedCategories ||
                !this.motherProductOptions.length
            );
        },

        allProductRowsForGridByKey() {
            return new Map(
                this.allProductRowsForGrid.map(product => [String(product.productKey), product])
            );
        },

        promotionType() {
            // ensure unit is the default e.g. in parkinglot creation mode
            // Use local isWeightPromotion data property which is initialized from promotion
            // and updated when user changes the radio button
            return this.isWeightPromotion ? productTypes.weight : productTypes.unit;
        },

        promotionHasProducts() {
            return get(this.promotion, 'products.length', 0) > 0;
        },
        debouncedAddProductToPromo() {
            return debounce(this.addProductToPromo, 600);
        },
        debouncedRemoveProductFromPromo() {
            return debounce(this.onRowDeselected, 600);
        },
        promotionNotSaved() {
            return (
                get(this.unsavedPromotion, [this.namespace, offer], false) ||
                this.namespace === 'default'
            );
        },
        maxProductsReachedOnSelectAll() {
            if (!this.gridApi) return this.maxProductsReached;

            const productCount =
                this.stagingAreaPromotion.products.length +
                this.filteredRowCount +
                this.addedProductAccumulatorKeys.size -
                this.removedProductAccumulatorKeys.size;

            if (this.promotion.splitPromotion) {
                return productCount >= this.generalConfig.maxProductsPerSplitPromotion;
            }

            return (
                this.generalConfig.maxProductsPerPromotion !== -1 &&
                productCount >= this.generalConfig.maxProductsPerPromotion
            );
        },
        maxProductsReached() {
            const productCount =
                this.stagingAreaPromotion.products.length +
                this.addedProductAccumulatorKeys.size -
                this.removedProductAccumulatorKeys.size;
            if (this.promotion.splitPromotion) {
                return productCount >= this.generalConfig.maxProductsPerSplitPromotion;
            }
            return (
                this.generalConfig.maxProductsPerPromotion !== -1 &&
                productCount >= this.generalConfig.maxProductsPerPromotion
            );
        },
        isAttributeSelectorReadOnly() {
            return this.isReadOnly || !this.stagingAreaPromotion.userSelectedCategories.length;
        },
        rules() {
            return [value => validators.required(value)];
        },

        isSelectAllDisabled() {
            return this.maxProductsReachedOnSelectAll;
        },

        isDisabledSelectHeader() {
            return this.isDisabledInput;
        },

        selectAllDisabledTooltip() {
            return this.$t('validation.failureMessages.promotionMaxProducts', {
                maxProductsPerPromotion: this.promotion.splitPromotion
                    ? this.generalConfig.maxProductsPerSplitPromotion
                    : this.generalConfig.maxProductsPerPromotion,
            });
        },

        headerTooltip() {
            if (this.isDisabledSelectHeader) {
                return '';
            }
            if (this.isSelectAllDisabled) {
                return this.selectAllDisabledTooltip;
            }
            return '';
        },

        selectionColumnDef() {
            return {
                suppressHeaderMenuButton: this.isReadOnly,
                maxWidth: 50,
                headerTooltip: this.headerTooltip,
                tooltipValueGetter: this.selectionTooltip,
                cellClass: ({ data }) =>
                    data && this.partialMotherKeys.has(String(data.productKey))
                        ? 'product-offer-group__mother-partial'
                        : '',
            };
        },
    },

    watch: {
        productsForPromotionProductOfferGroup() {
            this.scheduleGridRefresh();
        },
        hasUserSelectedCategories(hasCategories) {
            if (hasCategories || !this.gridApi) {
                return;
            }

            if (!this.gridApi.isDestroyed()) {
                this.gridApi.removeEventListener('filterChanged', this.onFilterChanged);
            }

            this.gridApi = null;
        },
        async expanded() {
            if (this.expanded && isEmpty(this.productsForPromotionProductOfferGroup)) {
                const productsForPromotion = await this.fetchProductsAndAttributesForPromotionProductOfferGroup(
                    {
                        productOfferGroup: this.productOfferGroup,
                        attributes: this.selectedProductAttributes,
                        returnDocuments: true,
                    }
                );

                this.productsForPromotionProductOfferGroup = cloneDeep(productsForPromotion);
            } else {
                this.viewSelectedOnly = false;
            }
        },

        numberOfTiers() {
            // Update column definitions to show prices for new tiers (or remove old prices)
            if (this.gridApi) {
                this.regenerateGridColumns();
            }
        },
    },

    async created() {
        this.selectedProductAttributes = map(this.getDefaultAttributes, 'attributeKey');
        this.isWeightPromotion = this.promotion.isWeightPromotion;
        this.setGridColumns();
        this.gridOptions.getRowClass = ({ data }) => {
            const key = data.productKey;
            if (
                (this.productKeysOnPromotion.has(key) &&
                    !this.productOfferGroup.products.includes(key)) ||
                !this.userCategoriesKeys.has(data.category)
            ) {
                return 'disabled-row';
            }
        };
        if (this.isReadOnly) {
            this.viewSelectedOnly = true;
        } else if (this.expanded) {
            this.productsForPromotionProductOfferGroup = await this.fetchProductsAndAttributesForPromotionProductOfferGroup(
                {
                    productOfferGroup: this.productOfferGroup,
                    attributes: this.selectedProductAttributes,
                    returnDocuments: true,
                }
            );
        }
    },
    mounted() {
        this.emitter.on(
            uxEvents.promotionProductsUpdatedForGridRefresh,
            this.handlePromotionProductsUpdatedForGridRefresh
        );

        if (this.$refs['pog-name']) {
            // validate the field on mount to show error state if empty
            this.$refs['pog-name'].blur();
        }
    },

    beforeUnmount() {
        this.emitter.off(
            uxEvents.promotionProductsUpdatedForGridRefresh,
            this.handlePromotionProductsUpdatedForGridRefresh
        );

        // Clear any pending grid refresh timer to avoid stale callbacks after unmount
        if (this.pendingRefreshTimer !== null) {
            clearTimeout(this.pendingRefreshTimer);
            this.pendingRefreshTimer = null;
        }

        if (this.gridApi && !this.gridApi.isDestroyed()) {
            this.gridApi.removeEventListener('filterChanged', this.onFilterChanged);
        }

        if (!this.promotionTabs.offer.cacheDOM && this.gridOptions.api) {
            // force-clear any references pointing to the grid when it gets removed from the DOM
            this.gridOptions.api.cleanDownReferencesToAvoidMemoryLeakInCaseApplicationIsKeepingReferenceToDestroyedGrid();
        }

        this.gridApi = null;
    },

    methods: {
        canUseGridApi() {
            return !!this.gridApi && !this.gridApi.isDestroyed();
        },

        addMotherProductKeys(products) {
            if (!this.motherProductSelectionEnabled) return products;

            return withMotherProductKeys(products, this.productsForPromotionProductOfferGroup);
        },

        ...mapActions('promotions', [
            'setStagingAreaField',
            'addProductsToPromotion',
            'updatePromotion',
            'setPromotionStoreWide',
            'setPromotionCategoryWide',
            'updateOfferMechanicDescription',
        ]),
        ...mapActions('products', [
            'fetchProducts',
            'fetchBigAmountOfProducts',
            'fetchProductAttributesForPromotion',
            'fetchProductsAndAttributesForPromotionProductOfferGroup',
        ]),
        ...mapMutations('promotions', [
            'setUnsavedPromotion',
            'setAddProductsInProgress',
            'setPromotionOverlay',
        ]),

        async handlePromotionProductsUpdatedForGridRefresh({
            products,
            added,
            source,
            fetchProductsAndAttributesForPromotionProductOfferGroup = false,
            overlayId,
        }) {
            // Only one product offer group will ever be expanded at one time
            if (this.expanded) {
                if (fetchProductsAndAttributesForPromotionProductOfferGroup) {
                    const productsForPromotion = await this.fetchProductsAndAttributesForPromotionProductOfferGroup(
                        {
                            productOfferGroup: this.productOfferGroup,
                            attributes: this.selectedProductAttributes,
                            returnDocuments: true,
                        }
                    );

                    this.productsForPromotionProductOfferGroup = cloneDeep(productsForPromotion);
                }

                if (source === 'attributeScopeUpdate') {
                    let productsKeyMap = this.addMotherProductKeys(
                        products.filter(p => p.productOfferGroupId === this.productOfferGroup._id)
                    ).reduce((acc, p) => {
                        acc[p.productKey] = p;
                        return acc;
                    }, {});
                    const rowData = [];
                    this.gridApi.setFilterModel(null);
                    this.gridApi.forEachNode(node => {
                        rowData.push(node.data);
                    });

                    const updatedItems = rowData.map(row => {
                        return {
                            ...(productsKeyMap[row.productKey] || row),
                            onCurrentProductGroup: !!productsKeyMap[row.productKey],
                        };
                    });
                    this.gridApi.applyTransaction({
                        add: [],
                        remove: [],
                        update: updatedItems,
                    });

                    // Sync checkbox selection state with the updated onCurrentProductGroup values.
                    const nodesToSelect = [];
                    const nodesToDeselect = [];
                    this.gridApi.forEachNode(node => {
                        if (node.data.onCurrentProductGroup) {
                            nodesToSelect.push(node);
                        } else {
                            nodesToDeselect.push(node);
                        }
                    });
                    if (nodesToSelect.length > 0) {
                        this.gridApi.setNodesSelected({
                            nodes: nodesToSelect,
                            newValue: true,
                            source: 'attributeScopeUpdate',
                        });
                    }
                    if (nodesToDeselect.length > 0) {
                        this.gridApi.setNodesSelected({
                            nodes: nodesToDeselect,
                            newValue: false,
                            source: 'attributeScopeUpdate',
                        });
                    }

                    productsKeyMap = null;
                    this.setViewSelectedOnly(true);
                    this.setPromotionOverlay({
                        overlayId,
                        value: false,
                    });
                    return;
                }

                if (added) {
                    const updatedItems = this.addMotherProductKeys(
                        products.filter(p => p.productOfferGroupId === this.productOfferGroup._id)
                    ).map(p => ({
                        ...p,
                        onCurrentProductGroup: true,
                    }));

                    const updatedKeys = new Set(updatedItems.map(item => item.productKey));

                    this.upsertGridData({ updatedItems }, () => {
                        // After adding new items, select their checkboxes
                        if (Array.isArray(updatedItems) && updatedItems.length > 0) {
                            const nodesToSelect = [];
                            this.gridApi.forEachNode(node => {
                                if (updatedKeys.has(node.data.productKey)) {
                                    nodesToSelect.push(node);
                                }
                            });

                            if (nodesToSelect.length > 0) {
                                this.gridApi.setNodesSelected({
                                    nodes: nodesToSelect,
                                    newValue: true,
                                    source: 'addProducts',
                                });
                            }
                        }
                    });
                } else {
                    const removedProductKeys = new Set(products);
                    const productsToUpdateWithMinimalData = this.productsForPromotionProductOfferGroup
                        .filter(
                            p =>
                                removedProductKeys.has(p.productKey) &&
                                this.filterByProductType(p.isUnitProduct)
                        )
                        .map(p => ({
                            ...p,
                            onCurrentProductGroup: false,
                        }));
                    this.upsertGridData({
                        updatedItems: productsToUpdateWithMinimalData,
                    });
                }

                // after applying parent changes dispaly only selected products
                if (fetchProductsAndAttributesForPromotionProductOfferGroup) {
                    this.setViewSelectedOnly(true);
                }
            }
        },

        async onCategoriesChange() {
            if (!this.isSelectedPromotionCategoryWide) {
                // on first category selection gridApi is not available
                if (this.canUseGridApi()) this.gridApi.setGridOption('loading', true);
                const productsForPromotion = await this.fetchProductsAndAttributesForPromotionProductOfferGroup(
                    {
                        productOfferGroup: this.productOfferGroup,
                        attributes: this.selectedProductAttributes,
                        returnDocuments: true,
                    }
                );

                this.productsForPromotionProductOfferGroup = cloneDeep(productsForPromotion);
            }
            if (this.showAttributesScopeSelector) {
                this.$refs['attribute-filter-panel']?.resetAttributesScopeProducts();
            }

            await this.updateOfferMechanicDescription({ namespace: this.namespace });
        },

        setGridColumns() {
            this.columnDefs = this.getOfferGroupColumns();
        },

        async regenerateGridColumns() {
            if (!this.canUseGridApi()) {
                return;
            }

            this.columnDefs = this.getOfferGroupColumns();
            this.gridApi.setGridOption('columnDefs', this.columnDefs);

            await nextTick();
            this.autoSizeAllColumns();
        },

        openBulkDialog() {
            this.$refs['bulk-product-key-dialog'].openDialog();
        },

        openMotherProductDialog() {
            if (!this.canUseGridApi()) return;

            const fullySelectedKeys = this.motherProductOptions
                .filter(option => {
                    const mother = this.allProductRowsForGridByKey.get(String(option.productKey));
                    return this.getSelectableFamily(mother).every(member =>
                        this.gridApi.getRowNode(String(member.productKey))?.isSelected()
                    );
                })
                .map(option => option.productKey);

            this.$refs['mother-product-dialog'].openDialog(fullySelectedKeys);
        },

        onMotherProductSelection({ toSelect, toDeselect }) {
            const toMothers = keys =>
                keys
                    .map(key => this.allProductRowsForGridByKey.get(String(key)))
                    .filter(
                        product =>
                            product &&
                            !product.onAnotherProductGroup &&
                            this.userCategoriesKeys.has(product.category)
                    );

            // Deselect first so a still-selected sub-mother keeps its branch.
            if (toDeselect.length) this.applyFamilySelection(toMothers(toDeselect), false);
            if (toSelect.length) this.applyFamilySelection(toMothers(toSelect), true);
        },

        autoSizeAllColumns() {
            if (!this.canUseGridApi()) {
                return;
            }

            this.gridApi.autoSizeAllColumns();
        },

        debouncedFilterChange: debounce(function(newFilter) {
            if (!this.canUseGridApi()) {
                return;
            }

            this.gridApi.setGridOption('quickFilterText', newFilter);
        }, 800),

        debouncedUpdateDescription: debounce(function(description) {
            this.setStagingAreaField({
                namespace: this.namespace,
                fieldPath: this.fieldPath,
                fieldName: 'description',
                value: description,
            });
            this.setUnsavedPromotion({ namespace: this.namespace, tab: offer, value: true });
        }, 800),
        onGridReady(params) {
            this.gridApi = markRaw(params.api);
            if (!this.expanded) {
                return;
            }
            this.gridApi.setGridOption('loading', true);
            this.autoSizeAllColumns();

            this.gridApi.addEventListener('filterChanged', this.onFilterChanged);
            // We've noticed the ag-grid is quite slow at adding lot's of data,
            // therefore we are rendering with 500 first to show the table quicker
            // and then when this data is rendered we load the rest.
            // It takes around 200ms to see these initial 500 rows and
            // around 3/4 seconds to see the rest. The grid is not usable until
            // all the data is loaded.
            const gridData = this.dataForGrid.slice(0, 500);
            this.gridApi.setGridOption('rowData', gridData);
            // Checks if any products are already selected as part of this promotion
            // If so, applies a "selected" filter on the grid
            if (gridData.some(elem => elem.onCurrentProductGroup)) {
                this.setViewSelectedOnly(true);
            }
            this.gridApi.applyColumnState({ state: this.defaultSortModel });

            this.gridApi.setGridOption('loading', false);
        },
        async firstDataRendered(params) {
            // Once the initial set of rows are rendered we then set the remaining data
            this.gridApi = markRaw(params.api);
            this.gridApi.setGridOption('rowData', this.dataForGrid);
            // Checks if any products are already selected as part of this promotion
            // If so, applies a "selected" filter on the grid
            if (this.dataForGrid.some(elem => elem.onCurrentProductGroup)) {
                this.setViewSelectedOnly(true);
            }
            this.gridApi.applyColumnState({ state: this.defaultSortModel });

            await nextTick();
            this.updateFilteredRowCount();

            // initial selection of products on the grid based on the promotion data
            const nodesToSelect = [];
            this.gridApi.forEachNode(node => {
                if (node.data?.onCurrentProductGroup) {
                    nodesToSelect.push(node);
                }
            });
            this.gridApi.setNodesSelected({
                nodes: nodesToSelect,
                newValue: true,
                source: 'initialSelection',
            });
        },

        isDisabledSelectColumn({ data }) {
            if (
                this.isDisabledInput ||
                data.onAnotherProductGroup ||
                !this.userCategoriesKeys.has(data.category) ||
                this.productsInProgress[data.productKey] ||
                (this.maxProductsReached && !this.productKeysOnPromotion.has(data.productKey))
            ) {
                return true;
            }

            return false;
        },

        getOfferGroupColumns() {
            const detailColumnGroups = this.getGridConfig({ context: 'offerGroup' }).details || [];
            const nonPromoPriceColumnGroups =
                this.getGridConfig({ context: 'offerGroup' }).nonPromoPrice || [];
            const promoPriceColumnGroups =
                this.getGridConfig({ context: 'offerGroup' }).promoPrice || [];

            const columnDefs = [
                {
                    field: 'onCurrentProductGroup',
                    hide: true,
                    filterParams: {
                        values: [true, false],
                    },
                },
                ...(this.motherProductSelectionEnabled
                    ? [
                          {
                              colId: 'productFamilyRole',
                              headerName: toSentenceCase(this.$tkey('productFamilyRole')),
                              width: 90,
                              filter: 'agSetColumnFilter',
                              valueGetter: ({ data }) =>
                                  data ? getProductFamilyRole(data, this.productFamilyIndex) : '',
                              valueFormatter: ({ value }) =>
                                  value ? this.$tkey(`productFamilyRoles.${value}`) : '',
                              cellRenderer: ({ value, valueFormatted }) => {
                                  if (!value) return '';
                                  const badge = document.createElement('span');
                                  badge.className = `product-family-role product-family-role--${value}`;
                                  badge.textContent = valueFormatted;
                                  return badge;
                              },
                          },
                          {
                              field: 'MotherProductKey',
                              headerName: 'Mother Product Key',
                              width: 160,
                              valueFormatter: ({ value }) => (isNil(value) ? '' : String(value)),
                          },
                      ]
                    : []),
                ...this.mapColumnGroups(detailColumnGroups),
                ...this.generateAttributeColumns(),
                ...this.mapColumnGroups(nonPromoPriceColumnGroups, {
                    currencySymbol: this.currencySymbol,
                }),
            ];

            for (let i = 0; i < this.numberOfTiers; i += 1) {
                columnDefs.push(
                    ...this.mapColumnGroups(promoPriceColumnGroups, {
                        tierIndex: i,
                        tier: i + 1,
                        reward: this.getTierReward(i),
                        currencySymbol: this.currencySymbol,
                        namespace: this.namespace,
                        productOfferGroupId: this.productOfferGroup._id,
                        fixAvgPromoPrice: this.fixAvgPromoPrice,
                        getPriceUpdateProducts: this.getPriceUpdateProducts,
                        ...(this.motherProductSelectionEnabled
                            ? { getReward: this.getTierReward }
                            : {}),
                    })
                );
            }
            return columnDefs;
        },

        getTierReward(tierIndex) {
            const productOfferGroups = get(
                this.stagingAreaPromotion,
                `offerMechanic.tiers[${tierIndex}].productOfferGroups`
            );

            return get(
                find(productOfferGroups, pog => pog._id === this.productOfferGroup._id),
                'rewards.0.type'
            );
        },

        generateAttributeColumns() {
            const columnGroup = [
                {
                    headerName: toSentenceCase(this.$tkey('headers.additionalAttributes')),
                    groupId: 'attributes',
                    headerGroupComponent: 'attributeSelectionHeader',
                    headerGroupComponentParams: {
                        selectedAttributes: this.selectedProductAttributes,
                        onSelectedAttributeChange: this.onSelectedAttributeChange,
                    },
                    openByDefault: true,
                    children: [
                        ...(this.selectedProductAttributes.length
                            ? this.selectedProductAttributes.map((attr, index) => {
                                  const selectedAttribute = this.attributeMetadata.find(
                                      availableAttribute => {
                                          return availableAttribute.attributeKey === attr;
                                      }
                                  );
                                  return {
                                      colId: `attribute-${index}`,
                                      headerName: selectedAttribute.attributeName,
                                      sortable: true,
                                      valueGetter: ({ data }) => {
                                          if (!data.attributes) {
                                              return;
                                          }
                                          const attribute =
                                              data.attributes.find(a => {
                                                  return a.attributeKey === attr;
                                              }) || {};

                                          return attribute.attributeValue;
                                      },
                                      columnGroupShow: 'open',
                                  };
                              })
                            : [
                                  {
                                      colId: `attribute-placeholder`,
                                      headerName: '',
                                      sortable: false,
                                      filter: false,
                                      columnGroupShow: 'open',
                                      width: ATTRIBUTE_GROUP_WIDTH,
                                  },
                              ]),
                        {
                            colId: `attribute-collapsed`,
                            headerName: '',
                            sortable: false,
                            filter: false,
                            columnGroupShow: 'closed',
                            minWidth: 30,
                        },
                    ],
                },
            ];

            return columnGroup;
        },

        async onSelectedAttributeChange(value) {
            this.selectedProductAttributes = value;

            if (!this.canUseGridApi()) {
                return;
            }

            this.gridApi.setGridOption('loading', true);
            this.regenerateGridColumns();
            const productsForPromotion = await this.fetchProductAttributesForPromotion({
                attributes: this.selectedProductAttributes,
                productsForPromotion: this.productsForPromotionProductOfferGroup,
                returnDocuments: true,
            });

            this.productsForPromotionProductOfferGroup = cloneDeep(productsForPromotion);
        },

        getRowNodeId(data) {
            return data.productKey;
        },

        getSelectedProducts() {
            if (!this.canUseGridApi()) {
                return [];
            }

            return this.gridApi.getSelectedNodes();
        },

        deletePOG() {
            this.$emit('delete', { productOfferGroupId: this.productOfferGroup._id });
        },

        upsertGridData(
            { newItems = [], removedItems = [], updatedItems = [] },
            callback = () => {}
        ) {
            if (this.gridApi && !this.gridApi.isDestroyed()) {
                // Using applyTransactionAsync allows the grid to be used while the transactions are being applied.
                // This means users can select other checkboxes while products updates are being applied to the grid
                // which can take a long time.
                this.gridApi.applyTransactionAsync(
                    {
                        add: newItems,
                        remove: removedItems,
                        update: updatedItems,
                    },
                    callback
                );
            }
        },

        async updateRowSelected({ newValue, data }) {
            const productKey = data.productKey;

            this.productsInProgress[productKey] = true;

            if (newValue) {
                if (this.addedProductAccumulatorKeys.has(productKey)) {
                    this.productsInProgress[productKey] = false;
                    return;
                }
                this.setAddProductsInProgress(true);
                this.removedProductAccumulatorKeys.delete(productKey);
                this.addedProductAccumulatorKeys.add(productKey);
                this.debouncedAddProductToPromo();
            } else {
                this.addedProductAccumulatorKeys.delete(productKey);
                if (!this.removedProductAccumulatorKeys.has(productKey)) {
                    this.removedProductAccumulatorKeys.add(productKey);
                }
                this.debouncedRemoveProductFromPromo();
            }
        },

        async addProductToPromo() {
            if (!this.addedProductAccumulatorKeys.size) {
                this.setAddProductsInProgress(false);
                return;
            }

            const uniqueProductKeys = Array.from(this.addedProductAccumulatorKeys);
            this.addedProductAccumulatorKeys.clear();

            let productData = [];
            try {
                productData = await this.fetchProducts({
                    params: {
                        where: { productKey: { $in: uniqueProductKeys } },
                    },
                    returnDocuments: true,
                    storeDocuments: false,
                });
            } catch (error) {
                uniqueProductKeys.forEach(key => {
                    this.productsInProgress[key] = false;
                });
                this.setAddProductsInProgress(false);
                throw error;
            }

            const productMap = productData.reduce((accumulator, product) => {
                accumulator[product.productKey] = product;
                return accumulator;
            }, {});

            const newProducts = [];
            uniqueProductKeys.forEach(productKey => {
                const product = productMap[productKey];
                if (!product) {
                    this.productsInProgress[productKey] = false;
                    return;
                }

                if (this.productKeysOnPromotion.has(productKey)) {
                    this.productsInProgress[productKey] = false;
                    return;
                }

                newProducts.push({
                    ...product,
                    productOfferGroupId: this.productOfferGroup._id,
                });
            });

            if (!newProducts.length) {
                this.setAddProductsInProgress(false);
                return;
            }

            await this.addProductsToPromotion({
                promotionId: this.namespace,
                newProducts,
                productOfferGroupId: this.productOfferGroup._id,
            });
            newProducts.forEach(newProduct => {
                this.productsInProgress[newProduct.productKey] = false;
            });
            const isProductsInProgress = Object.keys(this.productsInProgress).some(
                product => this.productsInProgress[product]
            );

            if (!isProductsInProgress) {
                this.setAddProductsInProgress(false);
            }

            this.updateFilteredRowCount();
        },
        async onRowDeselected(products = null) {
            const queuedRemovals = products
                ? new Set(products)
                : this.removedProductAccumulatorKeys;
            const uniqueRemovedProducts = Array.from(queuedRemovals);
            this.removedProductAccumulatorKeys.clear();

            if (!uniqueRemovedProducts.length) {
                this.updateFilteredRowCount();
                return;
            }

            this.$emit('removed', {
                removedProducts: uniqueRemovedProducts,
                productOfferGroupId: this.productOfferGroup._id,
            });
            uniqueRemovedProducts.forEach(removedProduct => {
                delete this.productsInProgress[removedProduct];
            });

            this.updateFilteredRowCount();
        },

        getPromoMargin(product, params) {
            if (!product.onCurrentProductGroup) {
                return null;
            }

            if (!product.commercialCosts) {
                return 0;
            }

            let productFromStagingArea = null;

            if (this.stagingAreaPromotion) {
                productFromStagingArea = this.stagingAreaPromotion.products.find(
                    sap => sap.productKey === product.productKey
                );
            }

            const calculateMarginFunction = this.featureAwareFactory.getCalculateMarginFunction();

            if (!product.promoPrices || !(params.tierIndex in product.promoPrices)) return 0;
            const promoMargin = calculateMarginFunction({
                product: productFromStagingArea || product,
                prices: (productFromStagingArea || product).promoPrices[params.tierIndex],
                useMinPrice: this.showOnlyMinimumPrices,
                excludeFunding: false,
            });

            return promoMargin;
        },

        getNonPromoMargin(product) {
            if (!product.onCurrentProductGroup) {
                return null;
            }

            if (!product.commercialCosts) {
                return 0;
            }

            const calculateMarginFunction = this.featureAwareFactory.getCalculateMarginFunction();

            const nonPromoMargin = calculateMarginFunction({
                product,
                prices: product.nonPromoPrices,
                useMinPrice: this.showOnlyMinimumPrices,
                excludeFunding: true,
            });

            return nonPromoMargin;
        },

        getPromoDiscount(product, params, { priceField }) {
            // Handle products which have not been selected for the product group.
            if (!product.onCurrentProductGroup) {
                return null;
            }

            // If prices aren't available, we cannot calculate discount so default to 0.
            if (!product.promoPrices || !(params.tierIndex in product.promoPrices)) return 0;

            const promoPrices = product.promoPrices[params.tierIndex];
            const nonPromoPrices = product.nonPromoPrices;

            // Identify the price to calculate the discount for from the column definition params.
            if (!promoPrices[priceField] || !nonPromoPrices[priceField]) {
                return 0;
            }

            const promoPrice = promoPrices[priceField];
            const nonPromoPrice = nonPromoPrices[priceField];

            if (nonPromoPrice === 0) return 0;

            return round((nonPromoPrice - promoPrice) / nonPromoPrice, 2);
        },

        setViewSelectedOnly(viewSelectedOnly = false) {
            this.viewSelectedOnly = viewSelectedOnly;
            if (!this.canUseGridApi()) return;
            const model = this.gridApi.getFilterModel() || {};

            if (viewSelectedOnly) {
                model.onCurrentProductGroup = {
                    filterType: 'set',
                    values: ['true'],
                };
            } else {
                model.onCurrentProductGroup = null;
            }

            this.gridApi.setFilterModel(model);
        },

        async onProductTypeChange(productType) {
            if (this.canUseGridApi()) {
                this.gridApi.setGridOption('loading', true);
            }
            await this.setPromotionType(productType);
            this.refreshGridData();
        },

        async setPromotionType(value) {
            this.isWeightPromotion = value === productTypes.weight;
            this.setStagingAreaField({
                namespace: this.namespace,
                fieldName: 'isWeightPromotion',
                value: this.isWeightPromotion,
            });
            this.setUnsavedPromotion({ namespace: this.namespace, tab: offer, value: true });
        },

        onPOGScopeChange(value) {
            if (value === pogScope.storeWide) {
                this.setPromotionStoreWide({ namespace: this.namespace, pogIndex: this.index });
            }

            if (value === pogScope.categoryWide) {
                this.setPromotionCategoryWide({ namespace: this.namespace, pogIndex: this.index });
            }
        },

        /**
         * Schedule a deferred grid refresh to avoid synchronous updates during AG Grid render lifecycle.
         * Coalesces multiple refresh requests into a single deferred callback.
         */
        scheduleGridRefresh() {
            // If a refresh is already scheduled, cancel it and reschedule
            // This ensures only one refresh happens for bursts of watcher events
            if (this.pendingRefreshTimer !== null) {
                clearTimeout(this.pendingRefreshTimer);
            }

            // Schedule the refresh to run in the next macrotask, after AG Grid render lifecycle completes
            this.pendingRefreshTimer = setTimeout(() => {
                this.refreshGridData();
                this.pendingRefreshTimer = null;
            }, 0);
        },

        async refreshGridData() {
            // Safety guards: ensure grid exists and is not destroyed, component is still mounted
            if (!this.gridApi || this.gridApi.isDestroyed()) {
                return;
            }

            this.gridApi.setGridOption('rowData', this.dataForGrid);
            this.gridApi.applyColumnState({ state: this.defaultSortModel });
            this.gridApi.setGridOption('loading', false);

            await nextTick();
            this.updateFilteredRowCount();
        },

        filterByProductType(isUnitProduct) {
            return this.isWeightPromotion ? !isUnitProduct : isUnitProduct;
        },
        processCellFromClipboard(params) {
            this.emitter.emit('paste', params);
        },

        // intercept ag-grid copy event
        // if we have data in staging area for the cell retun value from stging area
        // if not return value from the ag-grid data
        processCellForClipboard(params) {
            const producKey = get(params, 'node.data.productKey', null);
            const stagingAreaProduct = this.stagingAreaPromotion.products.find(
                pr => String(pr.productKey) === String(producKey)
            );
            if (isNil(stagingAreaProduct)) {
                return params.value;
            }

            // path example: promoPrices[1].avgPrice - this key we can use to get price from stagingArea
            const path = get(params, 'column.colDef.field');

            const avgPrice = get(stagingAreaProduct, path, null);
            if (!isNil(avgPrice)) {
                return avgPrice;
            }

            return params.value;
        },

        getPriceUpdateProducts(params) {
            if (!this.motherProductSelectionEnabled) return [params.data];

            // Grid row data can lose MotherProductKey after transactions, so resolve by key.
            const product =
                this.allProductRowsForGridByKey.get(String(params.data.productKey)) || params.data;

            return getPriceUpdateProducts({
                product,
                index: this.productFamilyIndex,
                productKeysOnOfferGroup: this.productKeysOnProductOfferGroup,
                motherProductSelectionEnabled: this.motherProductSelectionEnabled,
                reward: params.reward,
            }).filter(
                member =>
                    String(member.productKey) === String(params.data.productKey) ||
                    this.productKeysOnPromotion.has(member.productKey)
            );
        },

        fixAvgPromoPrice(params) {
            if (!this.motherProductSelectionEnabled) {
                // Fix avg price into min and max prices in the grid
                set(params.data, `promoPrices.${params.tierIndex}.minPrice`, params.value);
                set(params.data, `promoPrices.${params.tierIndex}.maxPrice`, params.value);
                const node = this.gridApi.getRowNode(String(params.data.productKey));
                this.gridApi.refreshCells({ rowNodes: node ? [node] : [] });
                return;
            }

            const products = params.products || [params.data];
            const rowNodes = updateGridProductPrices({
                products,
                tierIndex: params.tierIndex,
                value: params.value,
                getRowNode: productKey => this.gridApi.getRowNode(productKey),
            });
            const editedProductKey = String(params.data.productKey);
            const editedRowNodes = rowNodes.filter(
                rowNode => String(rowNode.data.productKey) === editedProductKey
            );
            const childRowNodes = rowNodes.filter(
                rowNode => String(rowNode.data.productKey) !== editedProductKey
            );

            this.gridApi.refreshCells({ rowNodes: editedRowNodes });
            this.gridApi.redrawRows({ rowNodes: childRowNodes });
        },
        updateFilteredRowCount() {
            if (!this.canUseGridApi()) {
                this.filteredRowCount = 0;
                return;
            }

            const availableItems = [];
            this.gridApi.forEachNodeAfterFilter(rowNode => {
                if (!this.productKeysOnPromotion.has(rowNode.data.productKey)) {
                    availableItems.push(rowNode);
                }
            });
            this.filteredRowCount = availableItems.length;
        },
        onFilterChanged() {
            this.updateFilteredRowCount();
            this.refreshPartialMotherIndicators();
        },

        onRowSelected(event) {
            const { source, node, data } = event;
            if (source !== 'checkboxSelected') return;

            // Clicking a partially selected mother completes its family, like a tri-state checkbox.
            const wasPartial = this.partialMotherKeys.has(String(data.productKey));
            this.applyFamilySelection([data], wasPartial || node.isSelected());
        },

        refreshPartialMotherIndicators() {
            if (!this.motherProductSelectionEnabled || !this.canUseGridApi()) return;

            const visibleKeys = new Set();
            this.gridApi.forEachNodeAfterFilter(node => {
                visibleKeys.add(String(node.data.productKey));
            });
            const mothers = this.allProductRowsForGrid.filter(product =>
                this.isCascadingMother(product)
            );
            const nextKeys = getPartiallySelectedMotherKeys(
                mothers,
                mother =>
                    this.getSelectableFamily(mother)
                        .slice(1)
                        .filter(child => visibleKeys.has(String(child.productKey))),
                productKey => !!this.gridApi.getRowNode(String(productKey))?.isSelected()
            );
            const changedNodes = [...this.partialMotherKeys, ...nextKeys]
                .filter(key => this.partialMotherKeys.has(key) !== nextKeys.has(key))
                .map(key => this.gridApi.getRowNode(key))
                .filter(Boolean);

            this.partialMotherKeys = markRaw(nextKeys);
            if (changedNodes.length) {
                this.gridApi.refreshCells({
                    rowNodes: changedNodes,
                    columns: ['ag-Grid-SelectionColumn'],
                    force: true,
                });
            }
        },

        isCascadingMother(product) {
            return (
                !!product &&
                this.motherProductSelectionEnabled &&
                isMotherRole(getProductFamilyRole(product, this.productFamilyIndex))
            );
        },

        getSelectableFamily(product) {
            if (!this.motherProductSelectionEnabled) return [product];

            return getProductFamily(product, this.productFamilyIndex).filter(
                member =>
                    String(member.productKey) === String(product.productKey) ||
                    (!member.onAnotherProductGroup && this.userCategoriesKeys.has(member.category))
            );
        },

        applyFamilySelection(rootProducts, isSelected) {
            if (!this.canUseGridApi()) return;

            const familyProducts = uniqBy(
                rootProducts.flatMap(product => this.getSelectableFamily(product)),
                'productKey'
            );
            const nodesToUpdate = [];
            const updatedProducts = familyProducts.map(product => {
                const updatedProduct = {
                    ...product,
                    onCurrentProductGroup: isSelected,
                };
                const familyNode = this.gridApi.getRowNode(String(product.productKey));

                if (familyNode && familyNode.isSelected() !== isSelected) {
                    nodesToUpdate.push(familyNode);
                }
                this.updateRowSelected({ newValue: isSelected, data: updatedProduct });

                return updatedProduct;
            });

            if (nodesToUpdate.length) {
                this.gridApi.setNodesSelected({
                    nodes: nodesToUpdate,
                    newValue: isSelected,
                    source: 'motherProductCascade',
                });
            }
            this.gridApi.applyTransaction({ update: updatedProducts });
        },

        onSelectionChanged(event) {
            this.refreshPartialMotherIndicators();
            if (!event.source?.includes('SelectAll')) return;
            if (!this.canUseGridApi()) return;

            let filteredSelectedCount = 0;
            this.gridApi.forEachNodeAfterFilter(node => {
                if (node.isSelected()) filteredSelectedCount += 1;
            });
            const isSelected = filteredSelectedCount > 0;
            this.selectUnselectAll(isSelected);
        },

        selectUnselectAll(isSelected) {
            if (!this.canUseGridApi()) return;

            const updates = [];
            this.gridApi.forEachNodeAfterFilter(rowNode => {
                const data = rowNode.data;
                if (isSelected !== !!data.onCurrentProductGroup) {
                    data.onCurrentProductGroup = isSelected;
                    updates.push(data);
                }
            });

            this.onSelectUnSelectAll({ updates, isSelected });

            // Use transaction for prevent trigger update event in multiple cells
            this.gridApi.applyTransaction({ update: updates });
        },

        async onSelectUnSelectAll({ updates, isSelected }) {
            this.addedProductAccumulatorKeys.clear();
            this.removedProductAccumulatorKeys.clear();

            const productsToUpdate = uniqBy(
                updates.flatMap(update => this.getSelectableFamily(update)),
                'productKey'
            );

            if (isSelected) {
                // filter out products which are already selected in other POGs
                const availableProducts = [];
                const productData = await this.fetchBigAmountOfProducts({
                    keys: productsToUpdate.map(product => product.productKey),
                });

                productData.forEach(p => {
                    const key = p.productKey;
                    if (
                        (this.productKeysOnPromotion.has(key) &&
                            !this.productOfferGroup.products.includes(key)) ||
                        !this.userCategoriesKeys.has(p.category)
                    ) {
                        // needed for checkbox to still be unselected
                        p.onCurrentProductGroup = false;
                    } else {
                        // actual array of products being saved
                        availableProducts.push({
                            ...p,
                            productOfferGroupId: this.productOfferGroup._id,
                        });
                    }
                });

                await this.addProductsToPromotion({
                    promotionId: this.namespace,
                    newProducts: availableProducts,
                    productOfferGroupId: this.productOfferGroup._id,
                });
            } else {
                await this.onRowDeselected(map(productsToUpdate, 'productKey'));
            }
        },

        getMainMenuItems(params) {
            if (
                params.column?.getColId() === 'ag-Grid-SelectionColumn' &&
                !this.isDisabledSelectHeader
            ) {
                return [
                    {
                        name: toSentenceCase(this.$t('actions.unselectAll')),
                        action: () => {
                            this.gridApi.deselectAll();
                        },
                    },
                ];
            }

            return params.defaultItems;
        },

        selectionTooltip({ data }) {
            const tooltip = this.maxProductsTooltip({ data });
            if (tooltip || !this.isCascadingMother(data)) return tooltip;

            return toSentenceCase(
                this.$tkey('selectsFamilyProducts', {
                    count: this.getSelectableFamily(data).length,
                })
            );
        },

        maxProductsTooltip({ data }) {
            return this?.maxProductsReached && !this.productKeysOnPromotion.has(data.productKey)
                ? this.$t('validation.failureMessages.promotionMaxProducts', {
                      maxProductsPerPromotion: this.promotion.splitPromotion
                          ? this.generalConfig.maxProductsPerSplitPromotion
                          : this.generalConfig.maxProductsPerPromotion,
                  })
                : this.tooltipValueGetter({ data });
        },

        tooltipValueGetter({ data }) {
            const key = data.productKey;
            if (
                this.productKeysOnPromotion.has(key) &&
                !this.productOfferGroup.products.includes(key)
            ) {
                return toSentenceCase(this.$tkey('onlyOnePOG'));
            }
            if (!this.userCategoriesKeys.has(data.category)) {
                return toSentenceCase(this.$tkey('noPermission'));
            }
        },
    },
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';

.product-offer-group {
    height: 100%;

    &__name {
        margin-bottom: 1rem;
        display: flex;
        &--label {
            font-size: 1.2rem;
            margin-top: 1.5rem;
            margin-right: 1rem;
        }
        &--wrapper {
            padding: 0.5rem 0 0.5rem 2rem;
        }
        &--text {
            :deep() {
                .v-field__field {
                    width: 20rem;
                    padding-left: 1rem;
                }
            }
        }
    }

    &__delete {
        display: flex;
        margin-right: 0.5rem;
    }

    :deep(.product-offer-group__mother-partial .ag-checkbox-input-wrapper::after) {
        content: var(--ag-icon-font-code-checkbox-indeterminate, '\f107');
        color: var(--ag-checkbox-indeterminate-color);
        display: var(--ag-icon-font-display-checkbox-indeterminate, var(--ag-icon-font-display));
    }

    &__category-selector {
        display: flex;
        height: 4rem;
        background-color: $promo-light-blue-4;
        margin-bottom: 1rem;
        padding-left: 3rem;
        padding-top: 0.5rem;
        column-gap: 1rem;

        &--text {
            padding-top: 0.5rem;
        }

        &--type {
            min-width: 10%;
        }

        &--categories {
            margin-top: 0 !important;
            align-self: flex-start;
        }
    }

    &__panel {
        border-bottom: $component-border;

        &--header {
            padding-left: 1rem;
            padding-right: 1rem;
            padding-top: unset;
            padding-bottom: unset;

            &--readonly {
                :deep(.v-expansion-panel-title__overlay) {
                    display: none;
                }
            }

            &--content {
                width: 100%;
                display: flex;
                justify-content: space-between;
            }
        }
    }

    &__filter {
        display: flex;
        align-items: baseline;

        :deep() {
            .v-input--selection-controls {
                margin: 0 2rem;
                padding-top: 0;
            }

            .v-input__control {
                flex-direction: row;
                align-items: center;

                .v-selection-control-group--inline {
                    margin-top: 0;
                }
            }
        }
    }

    &__add-bulk {
        margin-left: 0.5rem;
    }

    &__filter-bar {
        margin-bottom: 1rem !important;
        :deep() {
            .v-input__slot {
                width: 105rem;
            }
        }
    }

    :deep(.product-family-role) {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2rem;
        height: 2rem;
        border-radius: 0.4rem;
        color: $promo-white;
        font-size: 1.2rem;
        font-weight: 700;
        line-height: 1;

        &.product-family-role--mother {
            background-color: $product-family-mother-colour;
        }

        &.product-family-role--child {
            background-color: $product-family-child-colour;
            color: $promo-black;
        }

        &.product-family-role--orphan {
            background-color: $product-family-orphan-colour;
        }
    }

    :deep(.selection-disabled) {
        .ag-header-select-all {
            opacity: 0.5;
            pointer-events: none; /* Prevents interaction entirely */
            cursor: not-allowed;
        }
    }

    :deep() {
        .ag-header-cell-resize::after {
            content: '';
            position: absolute;
            z-index: 1;
            display: block;
            left: calc(50% - 0px) !important;
            width: 1px;
            height: 30%;
            top: calc(50% - 15%);
            background-color: var(--ag-header-column-resize-handle-color, rgba(186, 191, 199, 0.5));
        }

        .ag-header-select-all {
            margin-right: 0rem;
        }

        .ag-header-cell.ag-column-first {
            top: 0px !important;
        }

        .disabled-row {
            opacity: 0.5;
            cursor: not-allowed;
        }
        .v-select__slot {
            height: 2.8rem !important;
            min-width: 25rem;
        }
        // Centers the Select All header button
        .ag-header-cell:first-child {
            display: flex !important;
            justify-content: center !important;
        }

        .ag-checkbox-input-wrapper {
            font-size: 1.5rem;
        }

        .ag-cell {
            .ag-cell-wrapper {
                display: block;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }

        .ag-cell.ag-cell-value {
            display: block;
            padding-top: 0.6rem;
        }

        .ag-cell[col-id='ag-Grid-SelectionColumn'] {
            .ag-cell-wrapper {
                height: 100%;

                .ag-selection-checkbox {
                    height: 100%;
                }
            }
        }

        .ag-header-group-cell-label,
        .product-attribute-selector__header .summary-header__label {
            font-size: 1.3rem;
            font-weight: 700;
            font-family: 'Source Sans Pro';
        }
    }
}

:deep() {
    .ag-tooltip {
        color: $promo-tooltip-text-colour;
        border-radius: $promo-tooltip-border-radius;
    }
}
</style>
